import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Building2, UserPlus, ScrollText, LogOut, ArrowLeft, ShieldAlert, Menu } from "lucide-react";
import { useAuth } from "@/lib/auth";
import ThemeToggle from "@/components/ThemeToggle";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";

const navItems = [
  { to: "/admin", label: "Workspaces", icon: Building2, testid: "nav-platform-tenants", end: true },
  { to: "/admin/onboarding", label: "Onboard client", icon: UserPlus, testid: "nav-platform-onboarding" },
  { to: "/admin/audit", label: "Staff audit", icon: ScrollText, testid: "nav-platform-audit" },
];

/**
 * The platform console shell.
 *
 * Deliberately a sibling of Layout rather than a route inside it. Operating on
 * the whole fleet and operating inside one customer's workspace are different
 * jobs with different blast radii, and the chrome should never leave you
 * unsure which one you are doing — hence the different accent, the different
 * nav, and the banner. The customer's tenant switcher, plan pill and persona
 * lens are all absent because none of them mean anything here.
 */
export default function PlatformLayout() {
  const { user, logout, hasWorkspace } = useAuth();
  const nav = useNavigate();
  const [navOpen, setNavOpen] = React.useState(false);

  if (!user) { window.location.href = "/login"; return null; }

  // Shared by the fixed sidebar and the drawer used below lg.
  const sidebarContent = (onNavigate) => (
    <>
      <div className="px-5 py-5 flex items-center gap-2 border-b" style={{ borderColor: "var(--border)" }}>
        <div className="w-7 h-7 rounded-md flex items-center justify-center"
             style={{ background: "linear-gradient(135deg,#BF5AF2,#0A84FF)" }}>
          <ShieldAlert size={15} color="#fff" strokeWidth={2.4} />
        </div>
        <div>
          <div className="font-display text-[15px] leading-tight">Prioris</div>
          <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--risk-product)" }}>
            Platform
          </div>
        </div>
      </div>

      <nav className="px-2 py-3 flex flex-col gap-0.5">
        {navItems.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            data-testid={it.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] ${
                isActive ? "nav-link is-active" : "nav-link"
              }`
            }
            style={({ isActive }) => (isActive ? { background: "var(--surface-2)" } : {})}
          >
            <it.icon size={15} strokeWidth={1.8} />
            {it.label}
          </NavLink>
        ))}
      </nav>

      {hasWorkspace && (
        <div className="px-2 pt-2 mt-2 border-t" style={{ borderColor: "var(--border)" }}>
          <button
            onClick={() => { onNavigate?.(); nav("/"); }}
            data-testid="back-to-workspace"
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] nav-link"
          >
            <ArrowLeft size={15} strokeWidth={1.8} />
            Back to my workspace
          </button>
        </div>
      )}
    </>
  );

  return (
    <div className="flex min-h-screen" style={{ background: "var(--bg)" }}>
      <aside className="hidden lg:block w-[230px] shrink-0 border-r" style={{ borderColor: "var(--border)" }} data-testid="platform-sidebar">
        {sidebarContent()}
      </aside>
      <Sheet open={navOpen} onOpenChange={setNavOpen}>
        <SheetContent side="left" className="w-[260px] max-w-[85vw] p-0 overflow-y-auto"
                      style={{ background: "var(--bg)", borderColor: "var(--border)", color: "var(--text)" }}
                      aria-describedby={undefined} data-testid="platform-sidebar-drawer">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          {sidebarContent(() => setNavOpen(false))}
          <div className="px-5 py-4 border-t md:hidden flex" style={{ borderColor: "var(--border)" }}>
            <ThemeToggle />
          </div>
        </SheetContent>
      </Sheet>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-[56px] border-b flex items-center justify-between gap-2 px-3 md:px-6 shrink-0"
                style={{ borderColor: "var(--border)" }}>
          <button type="button" onClick={() => setNavOpen(true)} aria-label="Open navigation"
                  data-testid="nav-open-btn" className="lg:hidden p-2 -ml-1 rounded-md hover-wash shrink-0">
            <Menu size={18} />
          </button>
          {/* Not decoration. Every action taken here is attributed to this
              person by name and lands in a customer's own audit trail. */}
          <div className="flex items-center gap-2 px-2.5 py-1 rounded-md text-[11px] uppercase tracking-wider min-w-0 mr-auto lg:mr-0"
               style={{ background: "color-mix(in srgb, var(--risk-product) 12%, transparent)",
                        border: "1px solid color-mix(in srgb, var(--risk-product) 35%, transparent)",
                        color: "var(--risk-product)" }}
               data-testid="platform-banner">
            <ShieldAlert size={12} className="shrink-0" />
            <span className="truncate">
              Platform console<span className="hidden sm:inline"> · actions affect real customer workspaces</span>
            </span>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="hidden md:block"><ThemeToggle /></div>
            <span className="hidden xl:inline text-[12.5px]" style={{ color: "var(--text-secondary)" }} data-testid="platform-actor">
              {user.email}
            </span>
            <button onClick={logout} data-testid="logout-btn"
                    className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-md text-[12.5px] whitespace-nowrap"
                    style={{ background: "var(--surface)", border: "1px solid var(--border)",
                             color: "var(--text-secondary)" }}
                    aria-label="Sign out" title={user.email}>
              <LogOut size={13} /><span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
